import { useState, useEffect } from 'react';
import { Skull, Moon, Sun, Ghost, Key, Eye, EyeOff, Book, X, Check, Info, AlertTriangle } from 'lucide-react';

export default function NecrovaSimplified() {
  const [darkMode, setDarkMode] = useState(true);
  const [secretsRevealed, setSecretsRevealed] = useState(false);
  const [keyCount, setKeyCount] = useState(0);
  const [runeSequence, setRuneSequence] = useState([]);
  const [unlockedSecrets, setUnlockedSecrets] = useState([false, false, false]);
  const [bookOpen, setBookOpen] = useState(false);
  const [shadowWhispers, setShadowWhispers] = useState('');
  const [hoveredRune, setHoveredRune] = useState(null);
  
  // Predefined rune sequence and runes
  const correctRuneSequence = ["ᚦ", "ᛟ", "ᚺ", "ᛉ", "⸸"];
  const runes = ["⸸", "ᛟ", "ᚦ", "ᛉ", "ᚺ"];
  
  // Rune meanings
  const runeSecrets = {
    "⸸": "The Cross of Necrova: Symbol of the threshold between life and death",
    "ᛟ": "The Inheritance Rune: What was once will be again",
    "ᚦ": "The Thorn Rune: Pain breeds strength",
    "ᛉ": "The Protection Rune: Wards against unwanted spirits",
    "ᚺ": "The Whisper Rune: Carries messages to the beyond"
  };
  
  // Hints for different patterns
  const patternHints = [
    {
      name: "The First Gate",
      hint: "Five runes, beginning with the Thorn and ending with the Cross. Order matters.",
      completed: unlockedSecrets[0],
    },
    {
      name: "The Second Gate",
      hint: "Three keys hidden in different realms: darkness, knowledge, and the sacred symbol.",
      completed: unlockedSecrets[1],
    },
    {
      name: "The Final Revelation",
      hint: "When all other conditions are met, seek the phantom in the darkest corner.",
      completed: unlockedSecrets[2],
    }
  ];
  
  // Whisper fragments
  const whisperFragments = [
    "In darkness...",
    "seek the third key...",
    "when the moon is high...",
    "speak the ancient words...",
    "the veil will fall..."
  ];

  useEffect(() => {
    // Randomly trigger whispers in dark mode
    const whisperInterval = setInterval(() => {
      if (darkMode && Math.random() < 0.1) {
        const randomWhisper = whisperFragments[Math.floor(Math.random() * whisperFragments.length)];
        setShadowWhispers(prev => prev ? `${prev}\n${randomWhisper}` : randomWhisper);
      }
    }, 10000);
    
    return () => {
      clearInterval(whisperInterval);
    };
  }, [darkMode]);

  // Check if rune sequence matches the correct one
  useEffect(() => {
    if (runeSequence.length === correctRuneSequence.length) {
      const isCorrect = runeSequence.every((rune, index) => rune === correctRuneSequence[index]);
      
      if (isCorrect) {
        const newUnlocked = [...unlockedSecrets];
        newUnlocked[0] = true;
        setUnlockedSecrets(newUnlocked);
        setShadowWhispers(prev => prev + "\n" + "The first seal is broken...");
      } else {
        // Reset on incorrect sequence
        setRuneSequence([]);
        setShadowWhispers(prev => prev + "\n" + "The runes reject your pattern...");
      }
    }
  }, [runeSequence]);

  const toggleDarkMode = () => setDarkMode(!darkMode);
  
  const toggleBook = () => setBookOpen(!bookOpen);
  
  const findKey = (keyIndex) => {
    if (keyCount < 3) {
      // Make it more challenging - only specific keys can be found based on conditions
      if (keyIndex === 0 && darkMode) {
        setKeyCount(prev => prev + 1);
        setShadowWhispers(prev => prev + "\n" + "A key emerges from shadow...");
      } else if (keyIndex === 1 && bookOpen) {
        setKeyCount(prev => prev + 1);
        setShadowWhispers(prev => prev + "\n" + "Knowledge reveals a key...");
      } else if (keyIndex === 2 && hoveredRune === "⸸") {
        setKeyCount(prev => prev + 1);
        setShadowWhispers(prev => prev + "\n" + "The final key manifests...");
      }
      
      // Check if all keys found
      if (keyCount === 2) {
        const newUnlocked = [...unlockedSecrets];
        newUnlocked[1] = true;
        setUnlockedSecrets(newUnlocked);
      }
    }
  };
  
  const handleRuneClick = (rune) => {
    // Add the rune to the sequence
    setRuneSequence(prev => {
      const newSequence = [...prev, rune];
      if (newSequence.length > correctRuneSequence.length) {
        return [rune]; // Reset if too long
      }
      return newSequence;
    });
    
    // Visual feedback
    setShadowWhispers(prev => prev + "\n" + `Rune "${rune}" activated...`);
  };
  
  const unlockThirdSecret = () => {
    if (darkMode && keyCount >= 3 && unlockedSecrets[0] && unlockedSecrets[1]) {
      const newUnlocked = [...unlockedSecrets];
      newUnlocked[2] = true;
      setUnlockedSecrets(newUnlocked);
      setShadowWhispers(prev => prev + "\n" + "The final veil parts...");
    } else {
      setShadowWhispers(prev => prev + "\n" + "Not yet ready...");
    }
  };

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-950 text-gray-400' : 'bg-gray-800 text-gray-300'}`}>
      {/* Header with Toggle */}
      <header className={`p-4 flex justify-between items-center ${darkMode ? 'bg-black' : 'bg-gray-900'}`}>
        <div className="flex items-center">
          <Skull className={`mr-2 ${darkMode ? 'text-red-900' : 'text-red-700'}`} />
          <h1 className="text-2xl font-bold">NECROVA</h1>
        </div>
        <div className="flex items-center space-x-4">
          <button 
            onClick={toggleBook} 
            className="p-2 rounded-full bg-gray-900"
          >
            <Book size={20} />
          </button>
          <button 
            onClick={toggleDarkMode} 
            className={`p-2 rounded-full ${darkMode ? 'bg-gray-900 text-yellow-900' : 'bg-gray-800 text-yellow-600'}`}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow p-6 relative">
        {/* Book Overlay */}
        {bookOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
            <div className={`w-full max-w-2xl ${darkMode ? 'bg-gray-900' : 'bg-gray-800'} rounded-lg shadow-2xl overflow-hidden`}>
              {/* Book Header */}
              <div className={`${darkMode ? 'bg-black' : 'bg-gray-900'} p-4 flex justify-between items-center`}>
                <h2 className="text-xl font-bold flex items-center">
                  <Book size={20} className={`mr-2 ${darkMode ? 'text-red-900' : 'text-red-700'}`} />
                  Ancient Grimoire
                </h2>
                <button 
                  onClick={toggleBook}
                  className="p-1 rounded-full hover:bg-gray-800"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Book Content */}
              <div className="p-4 max-h-96 overflow-y-auto">
                <div className="mb-4 text-sm opacity-70">
                  <p>The grimoire reveals hints of the ancient patterns, but never their exact form...</p>
                </div>
                
                {/* Ritual Hints */}
                <h3 className="text-lg font-bold mb-2">Ritual Hints</h3>
                {patternHints.map((patternHint, index) => (
                  <div 
                    key={index} 
                    className={`mb-4 p-3 border rounded-md ${
                      patternHint.completed ? 
                        (darkMode ? 'border-red-900 bg-red-900 bg-opacity-10' : 'border-red-800 bg-red-800 bg-opacity-10') : 
                        'border-gray-800'
                    }`}
                  >
                    <div className="flex items-center mb-1">
                      {patternHint.completed ? (
                        <Check size={16} className="mr-2 text-green-600" />
                      ) : (
                        <AlertTriangle size={16} className="mr-2 text-yellow-600" />
                      )}
                      <h3 className="font-bold">{patternHint.name}</h3>
                    </div>
                    <p className="text-sm opacity-80">{patternHint.hint}</p>
                  </div>
                ))}
                
                {/* Rune Meanings */}
                <h3 className="text-lg font-bold mb-2 mt-6">Rune Meanings</h3>
                <div className="space-y-4">
                  {runes.map((rune, index) => (
                    <div key={index} className="flex items-start">
                      <div className={`text-4xl mr-4 ${darkMode ? 'text-red-900' : 'text-red-700'}`}>
                        {rune}
                      </div>
                      <div>
                        <h3 className="font-bold">{runeSecrets[rune].split(':')[0]}</h3>
                        <p className="text-sm opacity-80">{runeSecrets[rune].split(':')[1]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Book Footer */}
              <div className={`${darkMode ? 'bg-black' : 'bg-gray-900'} p-3 text-xs opacity-60 flex items-center`}>
                <Info size={12} className="mr-2" />
                <span>The secrets of Necrova are revealed only to those who truly seek them.</span>
              </div>
            </div>
          </div>
        )}
      
        {/* Welcome Message */}
        <div className="text-center relative z-10 mt-8">
          <h2 className={`text-6xl font-bold mb-4 ${darkMode ? 'text-red-900' : 'text-red-700'}`}>
            Welcome Home
          </h2>
          <p className="text-lg mb-8 opacity-60">The shadows have awaited your return...</p>
          
          {/* Rune Sequence Display */}
          {runeSequence.length > 0 && (
            <div className="mb-6 font-mono text-sm opacity-70">
              Current sequence: {runeSequence.join(' ')}
            </div>
          )}
          
          {/* Rune Section */}
          <div className="flex justify-center space-x-8 my-12">
            {runes.map((rune, index) => (
              <div 
                key={index} 
                className={`text-4xl cursor-pointer transition-all duration-500 hover:scale-110 ${
                  darkMode ? 'text-red-900 hover:text-red-700' : 'text-red-700 hover:text-red-500'
                } ${hoveredRune === rune ? 'scale-125' : ''}`}
                onMouseEnter={() => setHoveredRune(rune)}
                onMouseLeave={() => setHoveredRune(null)}
                onClick={() => handleRuneClick(rune)}
              >
                {rune}
              </div>
            ))}
          </div>
          
          {/* Shadow Whispers */}
          {shadowWhispers && (
            <div className={`mt-8 p-4 ${darkMode ? 'bg-black bg-opacity-40' : 'bg-gray-900 bg-opacity-40'} rounded-lg max-h-32 overflow-y-auto`}>
              <p className="font-mono text-xs text-gray-500 whitespace-pre-line">
                {shadowWhispers}
              </p>
            </div>
          )}
          
          {/* Secrets Section */}
          <div className="mt-16">
            <div className="flex justify-center items-center mb-6">
              <button
                onClick={() => setSecretsRevealed(!secretsRevealed)}
                className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                  darkMode ? 'bg-gray-900 hover:bg-gray-800' : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                {secretsRevealed ? (
                  <>
                    <EyeOff size={18} className="mr-2" />
                    Hide Ancient Secrets
                  </>
                ) : (
                  <>
                    <Eye size={18} className="mr-2" />
                    Reveal Ancient Secrets
                  </>
                )}
              </button>
            </div>
            
            {secretsRevealed && (
              <div className={`grid grid-cols-3 gap-6 max-w-4xl mx-auto ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <div className={`border p-4 rounded-lg ${unlockedSecrets[0] ? (darkMode ? 'border-red-900' : 'border-red-800') : 'border-gray-800'}`}>
                  <h3 className="text-lg font-bold mb-2">The Whispers Beyond</h3>
                  {unlockedSecrets[0] ? (
                    <p>The sequence is proven, and the first gate opens. Now seek the hidden keys that lurk in darkness.</p>
                  ) : (
                    <p>Locked. "Five runes in sacred order will break the seal."</p>
                  )}
                </div>
                <div className={`border p-4 rounded-lg ${unlockedSecrets[1] ? (darkMode ? 'border-red-900' : 'border-red-800') : 'border-gray-800'}`}>
                  <h3 className="text-lg font-bold mb-2">The Cryptic Keys</h3>
                  {unlockedSecrets[1] ? (
                    <p>Three keys found! The second gate opens. "Seek the phantom in deepest darkness to reveal the final truth."</p>
                  ) : (
                    <p>Locked. "Three keys lie hidden - in darkness, in knowledge, and in sacred symbol."</p>
                  )}
                </div>
                <div className={`border p-4 rounded-lg ${unlockedSecrets[2] ? (darkMode ? 'border-red-900' : 'border-red-800') : 'border-gray-800'}`}>
                  <h3 className="text-lg font-bold mb-2">The Final Revelation</h3>
                  {unlockedSecrets[2] ? (
                    <p>The circle is complete. The ancient ones recognize you as their own. "The words of power are: TENEBRIS ETERNUM NECROVUM."</p>
                  ) : (
                    <p>Locked. "When all paths are walked and all gates unlocked, the phantom will reveal all."</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Hidden Keys */}
        <div 
          className={`absolute cursor-pointer top-8 right-8 opacity-5 hover:opacity-30 transition-opacity duration-500 ${darkMode ? '' : 'invisible'}`} 
          onClick={() => findKey(0)}
        >
          <Key size={16} className="text-gray-700" />
        </div>
        
        <div 
          className={`absolute cursor-pointer bottom-16 left-12 opacity-5 hover:opacity-30 transition-opacity duration-500 ${bookOpen ? '' : 'invisible'}`} 
          onClick={() => findKey(1)}
        >
          <Key size={16} className="text-gray-700" />
        </div>
        
        <div 
          className="absolute cursor-pointer top-1/2 left-1/3 opacity-0 hover:opacity-10 transition-opacity duration-500" 
          onClick={() => findKey(2)}
        >
          <Key size={16} className="text-gray-700" />
        </div>
        
        {/* Hidden activation for third secret */}
        <Ghost 
          size={24} 
          className={`absolute bottom-8 right-8 cursor-pointer ${darkMode ? 'opacity-0' : 'opacity-0'} hover:opacity-20 transition-opacity duration-1000`}
          onClick={unlockThirdSecret}
        />
      </main>

      {/* Footer */}
      <footer className={`p-4 text-center ${darkMode ? 'bg-black text-gray-800' : 'bg-gray-900 text-gray-700'}`}>
        <p className="text-sm">
          {darkMode ? "Through darkness, we find truth" : "Even in light, shadows linger"}
        </p>
        {keyCount > 0 && (
          <p className="text-xs mt-1 opacity-50">Keys found: {keyCount}/3</p>
        )}
        {unlockedSecrets.filter(Boolean).length > 0 && (
          <p className="text-xs mt-1 opacity-50">Secrets unlocked: {unlockedSecrets.filter(Boolean).length}/3</p>
        )}
      </footer>
    </div>
  );
}